"use client";

import Link from "next/link";
import React, { useEffect, useMemo, useRef, useState } from "react";
import AncientScroll from "@/components/AncientScroll";


/**
 * Changelog & Recommendations (Tailwind version)
 * - Pure client-side; no backend
 * - Sticky global header + sticky per-section headers
 * - Horizontal TOC with scrollspy
 * - Hash deep links (#whats-new, #recommendations, ...)
 */

const SECTIONS = [
  {
    id: "whats-new",
    title: "What’s New",
    date: "2025-12-04",
    tag: "New",
    content: (
      <ul className="list-disc pl-6 space-y-1 text-sm text-textPrimary/80">
        <li>
          Introduced{" "}
          <Link
  href="/trust-security"
  className="text-primary underline underline-offset-2"
>
  Trust &amp; Security
</Link>
{" "}
          — learn exactly how ReloadPi keeps your funds and data SAFU.
        </li>
        <li>
          Added{" "}
          <strong>“Pay with Crypto (NOWPayments)”</strong> — you can now pay
          with multiple cryptocurrencies, with{" "}
          <span className="font-semibold text-accent">USDC (Solana)</span>{" "}
          recommended for the lowest fees.
        </li>
        <li>
          New trust badge and checkout hints so you always know which option is
          safest and cheapest.
        </li>
      </ul>
    ),
  },
  {
    id: "improvements",
    title: "Improvements",
    date: "2025-11-02",
    tag: "Improved",
    content: (
      <ul className="list-disc pl-6 space-y-1 text-sm text-textPrimary/80">
        <li>
          Added <code>PWA support</code> — you can now install the app on any
          device (via your browser’s “Add to Home Screen” option).
        </li>
        <li>
          Updated authentication system:{" "}
          <code>Extended session duration for logged-in users</code> and reduced
          refresh token lifetime for a better balance of convenience and
          security.
        </li>
        <li>
          <code>Hotels.com Gift Cards</code> are now available again — feel free
          to place bookings before December.
        </li>
        <li>
          <code>Steam Gift Cards</code> are temporarily unavailable while our
          distributor finalizes updated verification and stock controls. We'll
          re-enable sales once supply is confirmed stable.
        </li>
      </ul>
    ),
  },
  {
    id: "bug-fixes",
    title: "Bug Fixes",
    date: "2025-10-29",
    tag: "Fix",
    content: (
      <ul className="list-disc pl-6 space-y-1 text-sm text-textPrimary/80">
        <li>Resolved race condition in <code>PiSocket</code> teardown.</li>
        <li>Removed the Pi Price calculation from <strong>ProductList</strong>.</li>
        <li>Pi Price calculation now runs on card details <code>onClick</code>.</li>
      </ul>
    ),
  },
  {
    id: "recommendations",
    title: "Recommendations",
    date: "2025-12-04",
    tag: "Guide",
    content: (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          {
            h: "For Visa Prepaid Card",
            items: [
              "Start by tapping any other product before tapping Visa directly.",
              "We fetch product ranges dynamically, so a race condition may appear.",
              "After purchase, DO NOT open the link inside Pi Browser. Use the COPY button and open it in a regular browser instead.",
              "Share your experiences in our community forum.",
            ],
          },
          {
            h: "Lower Crypto Fees",
            items: [
              "On the crypto checkout, pick USDC (Solana) whenever possible.",
              "USDC on Solana has lower network fees compared to many other chains.",
              "If you pay in another coin, the provider may need to swap it to USDC, which can slightly increase the final amount.",
              "Always double-check the final total before confirming your payment.",
            ],
          },
          {
            h: "Your PI Coins Are SAFU",
            items: [
              "ReloadPi refreshes product data every 30 minutes to stay aligned with providers.",
              "If a failure appears, use the community forum or contact support — failed orders do not mean lost Pi.",
              "ReloadPi uses only the official Pi SDK for authentication.",
              "We do not sell or share your personal data; form fields are required only to fulfill your orders.",
            ],
          },
        ].map((c) => (
          <div
            key={c.h}
            className="rounded-xl border border-border bg-surface/80 p-4"
          >
            <h3 className="text-sm font-semibold text-textPrimary mb-2">
              {c.h}
            </h3>
            <ol className="list-decimal pl-5 space-y-1 text-sm text-textPrimary/80">
              {c.items.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "deprecations",
    title: "Deprecations",
    date: "2025-10-20",
    tag: "Heads-up",
    content: (
      <ul className="list-disc pl-6 space-y-1 text-sm text-textPrimary/80">
        <li>
          Old promo endpoint in <code>utils/api/promos.js</code> will be removed.
        </li>
        <li>
          Steam gift cards are <strong>not deprecated</strong>; they are
          temporarily offline pending distributor verification and stable stock.
          We'll post an update here when they return.
        </li>
      </ul>
    ),
  },
 {
  id: "roadmap",
  title: "Roadmap (Short-term)",
  date: "2025-11-15",
  tag: "Planned",
  content: (
    <ul className="list-disc pl-6 space-y-1 text-sm text-textPrimary/80">
      <li>
        Planned: <strong>E-book support</strong> — users will be able to purchase
        and store digital books (PDF, EPUB, etc.) through ReloadPi.
      </li>
      <li>
        Planned: <strong>AI agent for database</strong> with a direct endpoint
        that allows users to query and interact with data intelligently.
      </li>
    </ul>
  ),
},

];


function useScrollSpy(ids) {
  const [activeId, setActiveId] = useState(ids?.[0] || null);
  const observerRef = useRef(null);

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (observerRef.current) observerRef.current.disconnect();

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
          // update the URL hash without jump
          window.history.replaceState(null, "", `#${visible[0].target.id}`);
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    elements.forEach((el) => observer.observe(el));
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [ids]);

  return activeId;
}

export default function ChangelogPage() {
  const sections = useMemo(() => SECTIONS, []);
  const ids = sections.map((s) => s.id);
  const activeId = useScrollSpy(ids);

  // Jump to hash on mount
  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      const target = document.getElementById(hash.replace("#", ""));
      if (target) {
        setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      }
    }
  }, []);

  const backToTop = () => {
    (window.document.scrollingElement || window.document.documentElement)?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
     <AncientScroll className="px-2 sm:px-4 md:px-6" sealText="SAFU">
      
    <main className="max-w-[1100px] mx-auto px-5 py-5">
      {/* Global Header */}
      <div id="top" className="sticky top-0 z-30 bg-background text-textPrimary px-4 py-3 rounded-b-xl shadow-md border-b border-border">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold">Changelog & Recommendations</h1>
          <span className="text-xs text-textPrimary/150">Updated: {SECTIONS[0].date}</span>
        </div>

        {/* Horizontal TOC */}
        <nav className="mt-2 flex gap-2 overflow-x-auto pb-1" aria-label="Section navigation">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                const target = document.getElementById(s.id);
                if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={[
                "whitespace-nowrap rounded-full border px-3 py-1 text-sm transition",
                activeId === s.id
                  ? "bg-textPrimary text-background border-transparent"
                  : "text-textPrimary/90 hover:bg-surface border-border",
              ].join(" ")}
            >
              {s.title}
            </a>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="mt-4 space-y-3">
        {sections.map((s) => (
          <section
            key={s.id}
            id={s.id}
            aria-labelledby={`${s.id}-title`}
            className="rounded-xl border border-border bg-surface text-textPrimary scroll-mt-[72px]"
          >
            <header className="sticky top-14 z-10 flex items-center justify-between gap-2 bg-background/95 backdrop-blur border-b border-border rounded-t-xl px-4 py-3">
              <h2 id={`${s.id}-title`} className="text-base font-semibold">
                {s.title}
              </h2>
              <span className="text-[11px] px-2 py-0.5 rounded border border-border text-textPrimary/150">
                {s.tag} · {s.date}
              </span>
            </header>
            <div className="px-4 py-4">{s.content}</div>
            <div className="px-4 pb-4 text-right">
              <button
                onClick={backToTop}
                className="text-xs text-textPrimary/150 hover:text-textPrimary underline underline-offset-2"
              >
                Back to top ↑
              </button>
            </div>
          </section>
        ))}
      </div>

      {/* Floating Back-To-Top for long scrolls (desktop only) */}
      <button
        onClick={backToTop}
        className="hidden md:flex fixed bottom-6 right-6 items-center justify-center h-10 w-10 rounded-full border border-border bg-surface text-textPrimary/150 shadow hover:text-textPrimary"
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>
    </main>
      </AncientScroll>
  );
}
